import React, {PropsWithChildren, useState} from 'react';
import {Alert, Modal} from "react-bootstrap";
import {useField} from "formik";
import * as yup from "yup";
import {concepts, Group, Subscription} from "fearless-common";
import {Select, Loading} from "fearless-common-ui";
import {BackendForm, } from "../../forms";
import {backendEndpoints} from "../../../libs/backendEndpoints";
import Description from "../../Description";
import AmountSpan from "../AmountSpan";
import UnitSpan from "../UnitSpan";
import useGet from "../../hooks/useGet";
import TotalRow from "./TotalRow";
import GroupCreate from "../../../pages/partials/GroupCreate";
import OptionButton from "../OptionButton";
import styles from './SeatSelectorModal.module.scss';
import QuantitySelector from "../QuantitySelector";
import {StripePriceObject} from "../stripePriceObject";


function Totals({ selectedPrice }: { selectedPrice: StripePriceObject }) {
    const [error, setError] = useState('');
    const [{ value: qtyString },, { setValue }] = useField('quantity');

    const qty = parseInt(qtyString);

    const decrease = () => {
        if (qty <= 2) {
            setError('A team requires at least 2 seats')
        } else {
            if (error) setError('');
            setValue(Math.max(qty - 1, 2));
        }
    }
    const increase = () => {
        setValue(Math.max(1, qty + 1));
        if (error) setError('');
    }

    return (
        <div className="d-flex flex-column justify-content-center align-items-center">
            <QuantitySelector increase={increase} value={qty} decrease={decrease} />
            {error && <span className="text-danger">{error}</span>}
            <OptionButton selected={false}>
                <span className="text-primary fw-bold">Annual billing</span>
                <div className="d-inline-flex align-items-center me-2 pb-2">
                    <AmountSpan small price={selectedPrice} showMonthly className="text-primary" />
                    {selectedPrice.recurring && <UnitSpan price={selectedPrice} showMonthly showInterval />}
                </div>
            </OptionButton>
            {Number.isInteger(qty) && (
                <div className="d-flex flex-column justify-content-center align-items-center mt-4">
                    <TotalRow label="Total:">
                        <AmountSpan price={{ currency: selectedPrice.currency, unit_amount: qty * selectedPrice.unit_amount }} />
                        {selectedPrice.recurring && <span style={{ paddingBottom: '2px' }}><UnitSpan price={selectedPrice} showPerUser={false} showInterval /></span>}
                    </TotalRow>
                    <Description className="mt-2">VAT will be added based on billing address</Description>
                </div>
            )}
        </div>
    );
}

const GroupSelectorWrapper = ({ children, className = '' }: PropsWithChildren<{ className?: string }>) => (
    <div className={`mb-4 rounded ${className} ${styles.GroupSelectorWrapper}`}>{children}</div>
);

export default function SeatSelectorModal({ onCancel, selectedPrice, initGroupId = undefined }: {
    onCancel: () => void;
    initGroupId?: number;
    selectedPrice: StripePriceObject;
}) {
    const [groupId, setGroupId] = useState<number | undefined>(initGroupId);
    const { data: groups, isLoading, error, reload } = useGet<Array<Group & { subscriptions: Subscription[] }>>({
        endpoint: backendEndpoints.getGroupsWithSubscriptions,
        onSuccess: (groupsSuccess: Group[]) => {
            if (!groupId && Array.isArray(groupsSuccess) && groupsSuccess.length === 1) {
                setGroupId(groupsSuccess[0].id);
            }
        }
    });

    const initialValues = {
        quantity: 2,
        price_id: selectedPrice?.id,
        group_id: initGroupId ? `${initGroupId}` : '',
    };

    return (
        <Modal show onHide={onCancel} contentClassName="bg-neutral-200 border-0 shadow-lg">
            <Modal.Header closeButton>
                <Modal.Title className="text-center flex-grow-1">{selectedPrice.product.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading && <Loading />}
                {error && <Alert>{error}</Alert>}
                {Array.isArray(groups) && groups.length > 0 ? (
                    <BackendForm<typeof initialValues, { checkout_url?: string }>
                        targetEndpoint={groupId ? backendEndpoints.createAnnualGroupSubscription(groupId) : ''}
                        targetMethod="POST"
                        initialValues={initialValues}
                        validationSchema={yup.object({
                            group_id: yup.string().required('Please select a group'),
                        })}
                        showFooter={false}
                        onSuccess={(resp) => {
                            if (resp?.data?.checkout_url) {
                                window.location.href = resp.data.checkout_url;
                            }
                        }}
                    >
                        <GroupSelectorWrapper>
                            <Select
                                name="group_id"
                                onChange={(e) => setGroupId(parseInt(e.target.value))}
                                options={groups.map((g) => ({
                                    value: `${g.id}`,
                                    label: g.name,
                                    disabled: g.subscriptions?.length > 0
                                }))}
                                placeholder={`-- Select a ${concepts.Group} --`}
                            />
                        </GroupSelectorWrapper>
                        <b className="d-block text-primary mb-0 pb-0 line-height-1-0">Select number of seats:</b>
                        <small className="neutral-600 text-xs position-relative" style={{ top: -5 }}>(min. 2)</small>
                        <Totals selectedPrice={selectedPrice}/>

                        <BackendForm.Footer marginTop={false} buttonText="Next: Enter billing address" className="btn-action" center />
                    </BackendForm>
                ) : (
                    <GroupSelectorWrapper className="p-2">
                        <p>To have a {concepts.Group} {concepts.Subscription}, you must first create a group:</p>
                        <GroupCreate onSuccess={() => reload()} />
                    </GroupSelectorWrapper>
                )}
            </Modal.Body>
        </Modal>
    );
}
